import React, { lazy, Suspense } from 'react'
import { StandardLoader } from '@/components/StandardLoader'
import { TaskService } from '@duck/api-definitions'
import { MarkdownEditor, MultiPick, Button } from '@duck/ui'
import { WysiwygEditor } from '@/components/WysiwygEditor/WysiwygEditor'
import { TranslationKey, useTar } from '@/modules'
import { z } from 'zod'
import { TASK_PROFICIENCIES } from '@/utils/taskProficiencies'


const CodeInput = lazy(() => import('../DataWorkspace/CodeInput').then(module => ({ default: module.CodeInput })))

// Todo: Move to api-definitions
export const DataRecordSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()])
)

export const TableSchema = z.object({
  createTableQuery: z.string(),
  dataSets: z.array(z.array(DataRecordSchema))
})

export const DataBankSchema = z.record(
  z.string(),
  TableSchema
)

// Export validation function for use by parent components
export const validateDataBank = (dataBankString: string, t: (key: TranslationKey, params?: Record<string, unknown>) => string): string | null => {
  if (!dataBankString || dataBankString.trim() === '') {
    return t('errors.fieldRequired', { field: t('tasks.dataBank') })
  }

  try {
    const parsed = JSON.parse(dataBankString)
    DataBankSchema.parse(parsed)
    return null // Valid
  } catch (error) {
    if (error instanceof SyntaxError) {
      return t('errors.invalidJsonFormat')
    }
    if (error instanceof z.ZodError) {
      return t('errors.invalidDataBankFormat')
    }
    return t('errors.invalidDataBankFormat')
  }
}

interface TaskFormProps {
  formData: Partial<TaskService.Task>
  onChange: (field: keyof TaskService.Task, value: string | number | string[] | undefined) => void
  onSave: () => void
  onCancel: () => void
  onDelete?: () => void
  isSaving?: boolean
  saveError?: string | null
  fieldErrors?: Record<string, string>
  allAccessGroups: string[]
}

const SLUG_REGEX = /^[a-z]+(-[a-z]+)*$/

const DIFFICULTY_OPTIONS: readonly { value: string; label: TranslationKey }[] = [
  { value: 'Easy', label: 'tasks.difficulty.easy' },
  { value: 'Intermediate', label: 'tasks.difficulty.intermediate' },
  { value: 'Hard', label: 'tasks.difficulty.hard' },
  { value: 'Expert', label: 'tasks.difficulty.expert' },
] as const

export const TaskForm: React.FC<TaskFormProps> = ({
  formData,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isSaving = false,
  saveError,
  fieldErrors = {},
  allAccessGroups,
}) => {
  const { t } = useTar()

  // Ensure language is always set to SQL
  React.useEffect(() => {
    if (!formData.language || formData.language !== 'SQL') {
      onChange('language', 'SQL')
    }
  }, [formData.language])



  const handleDelete = () => {
    if (onDelete && formData.id) {
      const confirmed = window.confirm(
        `Are you sure you want to delete task "${formData.name || formData.id}"? This action can be undone, just not from here 😉`
      )
      if (confirmed) {
        onDelete()
      }
    }
  }

  const slugPlaceholder = React.useMemo(() => {
    const name = formData.name ?? ''
    return name
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase()
  }, [formData.name])

  const slugValidationError =
    formData.slug != null && formData.slug !== '' && !SLUG_REGEX.test(formData.slug)
      ? t('errors.invalidSlugFormat')
      : null

  const renderFormField = (
    label: TranslationKey,
    field: keyof TaskService.Task,
    type: 'text' | 'textarea' | 'number' | 'select' | 'markdown' = 'text',
    required = false,
    options?: readonly { value: string; label: TranslationKey }[],
    disabled = false,
    placeholderOverride?: string,
    errorOverride?: string
  ) => {
    const value = formData[field] ?? ''
    const placeholder = placeholderOverride ?? t('common.enterValue', { value: t(label).toLowerCase() })
    const baseInputClasses = `
      w-full px-3 py-2 text-sm border border-border rounded-md 
      bg-card text-foreground placeholder-secondary/60
      focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
      transition-colors
      ${disabled ? 'bg-secondary/5 text-muted-foreground cursor-not-allowed' : 'hover:border-secondary'}
    `

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">
          {t(label)}
          {required && <span className="text-error ml-1">*</span>}
        </label>
        {type === 'markdown' ? (
          <MarkdownEditor
            defaultValue={value as string}
            onChange={(content) => onChange(field, content)}
            placeholder={placeholder}
            className="max-h-[420px]"
            mdClassName="bg-background"
            mode={disabled ? 'view' : 'edit'}
          />
        ) : type === 'textarea' ? (
          <textarea
            value={value as string}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={disabled}
            className={`${baseInputClasses} resize-vertical`}
            rows={4}
            placeholder={placeholder}
            autoComplete="off"
          />
        ) : type === 'select' && options ? (
          <select
            value={value as string}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
            autoComplete="off"
          >
            <option value="">{t('common.select')} {t(label).toLowerCase()}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </select>
        ) : type === 'number' ? (
          <input
            type="number"
            value={value as number}
            onChange={(e) => onChange(field, parseFloat(e.target.value) || 0)}
            disabled={disabled}
            className={baseInputClasses}
            placeholder={placeholder}
            step="0.01"
            autoComplete="off"
          />
        ) : (
          <input
            type="text"
            value={value as string}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
            placeholder={placeholder}
            autoComplete="off"
          />
        )}
        {(errorOverride ?? fieldErrors[field]) && (
          <p className="text-sm text-error mt-1">{errorOverride ?? fieldErrors[field]}</p>
        )}
      </div>
    )
  }

  const proficiencies = React.useMemo(() => {
    return Object.values(TaskService.TaskProficienciesEnum).map((proficiency) => {
      const proficiencyConfig = TASK_PROFICIENCIES[proficiency]
      return {
        label: t(proficiencyConfig.label),
        value: proficiency,
        icon: proficiencyConfig?.icon(),
      }
    })
  }, [t])

  return (
    <form className="space-y-6" autoComplete="off" onSubmit={(e) => {
      e.preventDefault()
      onSave()
    }}>
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.basicInformation')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            {renderFormField('common.name', 'name', 'text', true)}
          </div>
          <div>
            {renderFormField('tasks.slug', 'slug', 'text', false, undefined, false, slugPlaceholder || undefined, fieldErrors.slug || slugValidationError || undefined)}
          </div>
          <div className="lg:col-span-2">
            {renderFormField('common.teaser', 'teaser', 'text', true)}
          </div>
        </div>
      </div>

      {/* Task Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.configuration')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {renderFormField('common.difficulty', 'difficulty', 'select', true, DIFFICULTY_OPTIONS)}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('common.language')}
              <span className="text-error ml-1">*</span>
            </label>
            <input
              type="text"
              value="SQL"
              disabled
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-secondary/5 text-muted-foreground cursor-not-allowed"
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('common.proficiencies')}
            </label>
            <MultiPick
              options={proficiencies}
              selected={formData.proficiencies || []}
              onChange={(vals) => onChange('proficiencies', vals)}
              placeholder={t('common.pickProficiencies')}
            />
            {fieldErrors.proficiencies && (
              <p className="text-sm text-error mt-1">{fieldErrors.proficiencies}</p>
            )}
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              {t('common.accessGroups')}
              <span className="text-error ml-1">*</span>
            </label>
            <MultiPick
              options={allAccessGroups.map((g) => ({ label: g, value: g }))}
              selected={Array.isArray(formData.accessGroups) ? (formData.accessGroups as string[]) : []}
              onChange={(vals) => onChange('accessGroups', vals)}
              placeholder={t('common.pickAccessGroups')}
              allowNewItem
            />
            {fieldErrors.accessGroups && (
              <p className="text-sm text-error mt-1">{fieldErrors.accessGroups}</p>
            )}
          </div>
          {renderFormField('common.seedFactor', 'seedFactor', 'number', true)}
          {renderFormField('common.availabilityWeight', 'availabilityWeight', 'number', false)}
          {renderFormField('tasks.sharedDBName', 'sharedDBName', 'text', false)}
          {renderFormField('tasks.customQueryTimeout', 'customQueryTimeout', 'number', false)}
        </div>
      </div>

      {/* SQL Queries */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('tasks.sqlQueries')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {renderFormField('tasks.defaultQuery', 'defaultQuery', 'textarea', true)}
          {renderFormField('tasks.dataQuery', 'dataQuery', 'textarea', true)}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <label className="block text-sm font-medium text-foreground">
              {t('tasks.solutionPlaceholder')}
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange('solutionPlaceholder', '')}
              disabled={isSaving}
            >
              {t('tasks.clearForDefaultPlaceholder')}
            </Button>
          </div>
          <WysiwygEditor
            content={formData.solutionPlaceholder ?? ''}
            onChange={(content) => onChange('solutionPlaceholder', content)}
            placeholder={t('common.enterValue', { value: t('tasks.solutionPlaceholder').toLowerCase() })}
            className="max-h-[240px] min-h-[180px] border border-border rounded-md bg-card"
          />
          {fieldErrors.solutionPlaceholder && (
            <p className="text-sm text-error mt-1">{fieldErrors.solutionPlaceholder}</p>
          )}
        </div>
      </div>

      {/* Data Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.dataConfiguration')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2 flex flex-col h-[240px]">
            <label className="block text-sm font-medium text-foreground">
              {t('tasks.feedbackPayload')}
            </label>
            <Suspense fallback={
              <div className="border border-border pt-2 bg-background h-[180px] flex items-center justify-center">
                <StandardLoader size="md" />
              </div>
            }>
              <CodeInput
                query={formData.feedbackPayload ?? ''}
                onQueryChange={(val) => onChange('feedbackPayload', val)}
                language={'JSON'}
              />
            </Suspense>
            {fieldErrors.feedbackPayload && (
              <p className="text-sm text-error mt-1">{fieldErrors.feedbackPayload}</p>
            )}
          </div>
          <div className="space-y-2 flex flex-col h-[240px]">
            <label className="block text-sm font-medium text-foreground">
              {t('tasks.dataBank')}
              <span className="text-error ml-1">*</span>
            </label>
            <Suspense fallback={
              <div className="border border-border pt-2 bg-background h-[180px] flex items-center justify-center">
                <StandardLoader size="md" />
              </div>
            }>
              <CodeInput
                query={formData.dataBank ?? ''}
                onQueryChange={(val) => onChange('dataBank', val)}
                language={'JSON'}
              />
            </Suspense>
            {fieldErrors.dataBank && (
              <p className="text-sm text-error mt-1">{fieldErrors.dataBank}</p>
            )}
          </div>
        </div>
      </div>

      {/* Description & Body */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.content')}
        </h3>
        <div className="grid grid-cols-1 gap-4">
          {renderFormField('common.description', 'description', 'markdown', true)}
          {renderFormField('common.body', 'body', 'markdown', true)}
        </div>
      </div>

      {/* Error Display */}
      {saveError && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-md">
          <p className="text-sm text-error">{saveError}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
        {/* Delete Button (Left Side) */}
        <div className="flex-1">
          {onDelete && formData.id && (
            <Button
              onClick={handleDelete}
              disabled={isSaving}
              variant="outline"
              className="text-error border-error hover:bg-error/10 order-3 sm:order-1"
            >
              {t('tasks.deleteTask')}
            </Button>
          )}
        </div>

        {/* Cancel and Save Buttons (Right Side) */}
        <div className="flex flex-col sm:flex-row gap-3 order-1 sm:order-2">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-md hover:bg-secondary/5 transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={isSaving || !!slugValidationError}
            className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isSaving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </div>
    </form>
  )
}
